import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvestorRelationsPage() {
  const [financials, setFinancials] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const finRes = await axios.get(`${API}/api/financials`);
        setFinancials(finRes.data);
      } catch (error) {
        console.error('Failed to fetch IR data:', error);
      }
    }
    fetchData();
  }, []);

  const calendarEvents = [
    { date: 'March 27, 2025', event: 'Annual Report 2024 Publication' },
    { date: 'April 15, 2025', event: 'Annual General Meeting' },
    { date: 'September 15, 2025', event: 'Half-Year Report 2025' },
  ];

  return (
    <div className="bg-white pt-32 pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Investor Relations</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#0A192F] tracking-tight mb-6">
            Financial Information
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Access our financial reports, key metrics, and corporate calendar. We are committed 
            to transparent communication with our shareholders and the investment community.
          </p>
        </motion.div>
      </div>

      {/* Key Metrics */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 border border-slate-200"
          >
            <div className="font-serif text-3xl text-[#0A192F] mb-1">
              {financials?.nav?.value || '£60M'}
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Registered Capital</div>
            {financials?.nav?.change && (
              <div className="text-sm text-emerald-600 mt-1">{financials.nav.change}</div>
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="p-6 border border-slate-200"
          >
            <div className="font-serif text-3xl text-[#0A192F] mb-1">{financials?.investments?.value || '12'}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Portfolio Companies</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="p-6 border border-slate-200"
          >
            <div className="font-serif text-3xl text-[#0A192F] mb-1">{financials?.employees?.value || '8,500+'}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Employees</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="p-6 border border-slate-200"
          >
            <div className="font-serif text-3xl text-[#0A192F] mb-1">{financials?.founded?.value || '2014'}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Founded</div>
          </motion.div>
        </div>
      </section>

      {/* Charts */}
      {financials?.highlights && (
        <section className="py-24 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight">Financial Development</h2>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-8 border border-slate-200"
              >
                <h3 className="font-medium text-[#0A192F] mb-6">Registered Capital (£M)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={financials.highlights}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
                      <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: '0' }} />
                      <Line type="monotone" dataKey="capital" stroke="#0A192F" strokeWidth={2} dot={{ fill: '#0A192F' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 border border-slate-200"
              >
                <h3 className="font-medium text-[#0A192F] mb-6">Portfolio Revenue (£M)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={financials.highlights}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
                      <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: '0' }} />
                      <Bar dataKey="revenue" fill="#0A192F" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Calendar */}
      <section className="py-24 bg-[#0A192F]" id="calendar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="font-serif text-3xl text-white tracking-tight">Corporate Calendar</h2>
          </motion.div>

          <div className="space-y-4">
            {calendarEvents.map((event, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center gap-6 p-6 border border-white/10"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <span className="text-white font-medium">{event.date}</span>
                </div>
                <span className="text-slate-400">{event.event}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact IR */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight mb-6">
              Investor Relations Contact
            </h2>
            <p className="text-slate-600 mb-8">
              For investor inquiries, please contact our Investor Relations team at BNDV Capital.
            </p>
            <a
              href="mailto:ir@bndvcapital.com"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#0A192F] text-white text-sm uppercase tracking-wider hover:bg-[#112240] transition-colors"
            >
              ir@bndvcapital.com
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
